import React, { useState, useMemo, useCallback } from 'react';
import { formatGroupDate, EarningsTileList } from './EarningsDateGroup';
import { useEarningsDay } from './useEarningsData';
import { useStockNews } from './useStockNews';
import { useCallHighlights } from '../../services/api';

// Companies reporting on a single date, opened by clicking a histogram bar.
// Reuses EarningsTile so labels/signals/options/expand behave exactly like the
// earnings list; bellwethers (from the histogram payload) are pinned to the top.
const EarningsDayPanel = ({ date, bellwethers = [], onClose, loggedInUser, onLoginSuccess, onOpenLabelManager }) => {
  const [expandedTicker, setExpandedTicker] = useState(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useEarningsDay(date);

  const stocks = useMemo(() => {
    const all = data?.pages?.flatMap((page) => page.stocks || []) || [];
    if (!bellwethers.length) return all;
    const rank = new Map(bellwethers.map((t, i) => [t, i]));
    return [...all].sort((a, b) => {
      const ra = rank.has(a.symbol) ? rank.get(a.symbol) : Infinity;
      const rb = rank.has(b.symbol) ? rank.get(b.symbol) : Infinity;
      return ra - rb; // bellwethers first (cap order); rest keep API order (stable sort)
    });
  }, [data?.pages, bellwethers]);

  const total = data?.pages?.[0]?.total || 0;
  const symbols = useMemo(() => stocks.map(s => s.symbol), [stocks]);
  const { data: newsMap } = useStockNews(symbols);
  const { data: highlightsMap } = useCallHighlights(symbols, loggedInUser?.email);

  const handleToggleTicker = useCallback((ticker) => {
    setExpandedTicker((prev) => (prev === ticker ? null : ticker));
  }, []);

  return (
    <div style={{ padding: '0 16px', marginBottom: 16 }}>
      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0',
        padding: '12px 0 12px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 10px' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
            {formatGroupDate(date)}
          </span>
          <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
            {total} stock{total !== 1 ? 's' : ''} reporting
          </span>
          {bellwethers.length > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#d97706', background: '#fef3c7',
              padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
            }}>
              {bellwethers.slice(0, 4).join(' · ')}{bellwethers.length > 4 ? ` +${bellwethers.length - 4}` : ''}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close day panel"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: '#9ca3af', fontSize: 18, fontWeight: 700, lineHeight: 1, padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '20px 0' }}>
            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>Loading companies…</span>
          </div>
        )}

        {/* Empty */}
        {!isLoading && stocks.length === 0 && (
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '16px 0' }}>
            No companies found for this date
          </p>
        )}

        {/* Tiles */}
        {stocks.length > 0 && (
          <EarningsTileList
            stocks={stocks}
            loggedInUser={loggedInUser}
            expandedTicker={expandedTicker}
            onToggleTicker={handleToggleTicker}
            onLoginSuccess={onLoginSuccess}
            onOpenLabelManager={onOpenLabelManager}
            newsMap={newsMap}
            highlightsMap={highlightsMap}
          />
        )}

        {/* Show more */}
        {hasNextPage && (
          <div style={{ textAlign: 'center', paddingTop: 12 }}>
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              style={{
                border: '1px solid #e5e7eb', background: '#fff', borderRadius: 999,
                padding: '6px 18px', fontSize: 12, fontWeight: 600,
                color: '#4f46e5', cursor: isFetchingNextPage ? 'wait' : 'pointer',
              }}
            >
              {isFetchingNextPage ? 'Loading…' : `Show more (${stocks.length} of ${total})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(EarningsDayPanel);
